import "./boot/publicUrl";           // the public build drops the debug URL flags — must stay the first import
import "./infra/perfProbeInstall";   // M44: ?debug only — must come straight after it
import { perfProbe } from "./infra/perfProbe";
import "./ui/styles.css";
import "./ui/keep.css";
import { LevelCatalog, OfflineSeedProvider, SaveRecord, SaveService, SaveStore, SuspendedLevel, initGearSeen } from "@highmarch/app";
import { BalanceData, loadBalance } from "@highmarch/domain";
import { Settings, SettingsSheetActions, onSettingsChange, showSettings } from "./ui/settings";
import { ChestStage } from "./render/chestStage";
import { LOOKS } from "./render/look";
import { GroundMesh } from "./render/groundMesh";
import { HqMaterials } from "./render/hqMaterials";
import { CpuRelease } from "./render/cpuRelease";
import { biomeOf } from "./ui/chapters";
import { FoliageView } from "./render/views/foliage";
import { DetailScatter } from "./render/views/detail";
import { audio } from "./audio/audio";
import { showReadabilitySheet } from "./render/sheet";
import { consoleLogger, systemClock } from "./infra/adapters";
import { openSaveStore } from "./infra/save/openSaveStore";
import type { LevelController } from "./level/levelController";
import { SceneRig } from "./render/scene";
import { AssetLibrary } from "./render/assets";
import { hubAssetNeeds } from "./render/assetNeeds";
import { setAssetLibrary } from "./render/views";
import type { HubScene } from "./render/hubScene";
import { showBootError } from "./ui/loading";
import { copyDiagnostics } from "./infra/diagnostics";
import { BuildWatcher } from "./infra/buildWatcher";
import { ScreenWakeLock } from "./infra/wakeLock";
import { requestWholeGame } from "./infra/offlineCache";
import { PerfOverlay } from "./ui/perfOverlay";
import { TitleScreen, removeBootSplash } from "./ui/title";
import { SHOWCASE, TitleDiorama } from "./render/titleDiorama";
import { cloudWipe } from "./ui/transition";
import { LevelLoading, StartGuard } from "./ui/levelLoading";
import type { HubView } from "./ui/hub/hubView";
import { toast } from "./ui/toast";
import type { ToastHandle } from "./ui/toast";
import { reloadFor, reloadNoteText, takeReloadNote } from "./infra/reloadNote";
import { frameJournal, installFrameJournal, journalLineText } from "./infra/frameJournal";
import { breadcrumbs, crashToastText, CrumbFacts } from "./infra/breadcrumbs";
import { glContexts } from "./infra/glContexts";
import { guide } from "./ui/guide/guide";
import { bindContinueRow } from "./ui/hub/battleCard";
import { ProgressKeeper } from "./keeper";
import { GameServices, Nav, Screens } from "./boot/context";
import { DEBUG } from "./boot/debug";
import { HubFlow } from "./boot/hubFlow";
import { LevelFlow } from "./boot/levelFlow";
import { MetaFlow } from "./boot/metaFlow";
import { installFeatures } from "./features/registry";
import { FEATURES } from "./features";
import balanceText from "../../../balance/generated/balance.json?raw";
import pkgText from "../package.json?raw";
import { installKitArt } from "./ui/kitArt";
// Last: the shop skin's screen layer follows every screen stylesheet it re-targets (styles/shopskinScreens.css).
import "./ui/styles/shopskinScreens.css";

/**
 * Composition root: wires the adapters, the save, the catalog and the features (features/registry.ts), then hands the
 * screens to three flows — boot/hubFlow.ts (the hub and the campaign map), boot/levelFlow.ts (a level from PLAY to its
 * end) and boot/metaFlow.ts (the save, Daily Orders, Captain Bram) — and keeps the boot, the title and the page's
 * lifecycle for itself.
 */
class Game implements Nav {
  private readonly g: GameServices;
  private readonly screens = new Screens();
  private readonly meta: MetaFlow;
  private readonly hubFlow: HubFlow;
  private readonly levelFlow: LevelFlow;

  constructor(app: HTMLElement, saveStore: SaveStore) {
    const canvas = document.createElement("canvas");
    canvas.id = "view";
    app.append(canvas);
    const ui = document.createElement("div");
    ui.id = "ui";
    app.append(ui);
    const rig = new SceneRig(canvas);
    const params = new URLSearchParams(location.search);
    // `?lite`: the cheapest picture that still exercises every code path — no shadows, bloom or ground scatter, a
    // third of the resolution. The CI smoke test runs on software GL, where the full scene takes seconds a frame and clicks time out.
    if (params.has("lite")) {
      rig.setShadows(false); rig.setBloom(false); rig.setPixelRatio(0.35); DetailScatter.density = 0;
    }
    const balance: BalanceData = loadBalance(balanceText);
    const saves = new SaveService(saveStore, systemClock, consoleLogger);
    // A save the catalog cannot open is backed up and replaced (the save notice says so), never a boot that dies.
    const loaded = saves.load(() => crypto.randomUUID(), (r) => { LevelCatalog.fromSave(balance, r); });
    const save: SaveRecord = loaded.record;
    if (loaded.outcome === "fresh") saves.save(save);
    consoleLogger.log("info", `save: ${loaded.outcome} player ${save.playerId}`);
    const catalog = LevelCatalog.fromSave(balance, save);
    // The Armoury's NEW tags: a save from before them starts with everything it holds already seen.
    if (initGearSeen(save, catalog.profile.gear)) saves.save(save);
    const keeper = new ProgressKeeper({ balance, saves, save, catalog, ui, persist: () => this.persist() });
    this.g = {
      balance, saves, save, catalog, keeper, rig, ui, seeds: new OfflineSeedProvider(), wakeLock: new ScreenWakeLock(),
      keepAllHq: params.has("allhq") || params.has("sheet"),
      version: `v${(JSON.parse(pkgText) as { version: string }).version} · web · ${__BUILD_STAMP__} UTC`,
      starts: new StartGuard(),
    };
    this.meta = new MetaFlow(this.g, this.screens, this);
    this.hubFlow = new HubFlow(this.g, this.screens, this.meta, this);
    this.levelFlow = new LevelFlow(this.g, this.screens, this.meta, this.hubFlow, this);
    installFeatures(FEATURES, { balance, catalog, save, ui, debug: DEBUG, now: () => systemClock.utcNowSeconds(), persist: () => this.persist() });
    this.meta.installGuide();
    bindContinueRow(ui, { onContinue: () => { guide()?.signal("playPressed"); this.continueSuspended(); }, onAbandon: () => void keeper.confirmAbandon(null).then((gone) => { if (gone) this.screens.hub?.rerender(); }) });
    rig.onRebuild = () => this.rebuildGraphics();
    void this.boot().catch(bootFailed);
    this.syncPerfOverlay();
    onSettingsChange(() => this.syncPerfOverlay());
  }

  // ---------------------------------------------------------------- Nav (the flows' calls into each other)

  showMenu(): void | Promise<void> { return this.hubFlow.showMenu(); }
  requestStart(level: number): void { this.levelFlow.requestStart(level); }
  continueSuspended(): void { this.levelFlow.continueSuspended(); }
  startLevel(level: number, resume: SuspendedLevel | null = null): Promise<void> { return this.levelFlow.startLevel(level, resume); }
  persist(): void { this.meta.persist(); }
  settingsActions(): SettingsSheetActions { return this.meta.settingsActions(); }
  openGuide(): void { this.meta.openGuide(); }
  resetGuide(): void { this.meta.resetGuide(); }

  // ---------------------------------------------------------------- the capture scripts' hooks (window.__td.game)

  get rig(): SceneRig { return this.g.rig; }
  get catalog(): LevelCatalog { return this.g.catalog; }
  get save(): SaveRecord { return this.g.save; }
  get hub(): HubView | null { return this.screens.hub; }
  get hubScene(): HubScene | null { return this.screens.hubScene; }
  get currentLevel(): LevelController | null { return this.screens.level; }
  /** Debug hooks for the browser checks: the install card regardless of the week, and the away card with sample lines. */
  debugInstallCard(): Promise<void> { return this.g.keeper.installCard(systemClock.utcNowSeconds(), true); }
  debugAwayCard(): Promise<void> { return this.g.keeper.debugAwayCard(); }

  // ---------------------------------------------------------------- boot and title

  /** Settings → Performance overlay, or `?stats` in the URL. */
  private perf: PerfOverlay | null = null;
  private syncPerfOverlay(): void {
    const want = Settings.current.perfHud || new URLSearchParams(location.search).has("stats");
    if (want && !this.perf) this.perf = new PerfOverlay(this.g.ui, this.g.rig, () => this.screens.level);
    else if (!want && this.perf) { this.perf.dispose(); this.perf = null; }
  }

  private async boot(): Promise<void> {
    const params = new URLSearchParams(location.search);
    // A reload the game made itself (an update, a lost GPU context) comes back to the hub, not the launch screen.
    const back = takeReloadNote();
    if (back) frameJournal.note(`reloaded: ${back.reason}`);
    // M33: the last session's breadcrumb — a page iOS closed under the player is recorded and gets a word on the hub
    const crash = breadcrumbs.boot(back !== null);
    if (crash) {
      const b = crash.crumb;
      consoleLogger.log("warning", `last session ${crash.kind === "foreground" ? "was closed by iOS while playing" : "was reclaimed in the background"}: ${b.screen}${b.level !== null ? ` L${b.level}` : ""}${b.stage !== null ? ` stage ${b.stage}` : ""}, contexts ${b.contexts.join("+") || "none"}${b.lastLoss ? `, last loss ${b.lastLoss}` : ""}`);
      frameJournal.note(`previous session ended: ${crash.kind === "foreground" ? "closed by iOS" : "reclaimed in the background"}`);
    }
    this.bootNotice = crashToastText(crash, this.g.keeper.suspended !== undefined);
    this.startBreadcrumbs();
    const sheet = params.has("sheet"), skip = params.has("skiptitle") || back !== null;
    // One screen from the first paint to TAP TO START (owner, 2026-10-06): the title in its booting state — the
    // showcase still (the launch image's picture) with the logo in its place and the loading plate where TAP TO START
    // will be. The live diorama is built behind it as soon as its own models are in (they load first), so the title
    // is usually already playing by the time the rest has loaded.
    // M49: a launch iOS closed under the title (the boot's memory peak: the live diorama built while the hub's models
    // decode) comes back on the still alone, so a phone near its limit is not caught in a reload loop on the title
    // ("86 … back to 0 … 88 … back to 0 … 90")
    const titleCrashed = crash !== null && ["title", "boot", "loading"].includes(crash.crumb.screen);
    if (titleCrashed) frameJournal.note("last launch ended on the title: no live diorama this time");
    const live = TitleDiorama.wanted() && !this.g.keepAllHq && !titleCrashed;
    const title = sheet || skip ? null : this.makeTitle(live);
    this.screens.title = title;
    const offline = registerOfflineCache();
    if (import.meta.env.PROD && !navigator.webdriver) void new BuildWatcher(() => this.onNewBuild()).start();
    const inlined = (window as unknown as { __TD_ASSETS?: Record<string, string> }).__TD_ASSETS;
    if (inlined) AssetLibrary.inlined = inlined;
    void audio.load((u) => AssetLibrary.resolveInlined(u));   // alongside the models, so the hub bed is ready by the title
    const lib = new AssetLibrary();
    this.screens.lib = lib;
    const ktx2 = lib.enableKtx2(this.g.rig.renderer);
    // Fonts finish before the first real screen so text never paints in the fallback face and then jumps (font swap flicker).
    const fonts = document.fonts?.load ? Promise.all(["400 16px 'Fredoka Display'", "700 16px Fredoka"].map((f) => document.fonts.load(f))).catch(() => undefined) : Promise.resolve();
    // The hub stands in the current chapter's biome (the lobby: "you're in the marsh now" without a word), so its
    // terrain maps and props are that biome's from the first frame; a level sets it again for its own chapter.
    const hubBiome = biomeOf(this.hubFlow.nextLevel());
    FoliageView.biome = DetailScatter.biome = hubBiome;
    const progress = (d: number, n: number) => title?.progress(d, n);
    const hubSet = (shipped: ReadonlySet<string>) => hubAssetNeeds(this.hubFlow.hubAssetInput(), shipped);
    if (live && title) {
      // The showcase's ground and models first (render/titleDiorama.ts), then the hub's. Its ground is the meadow's:
      // a hub in another biome gets its own maps under the clouds after TAP (enterHubGround), never both at once.
      await GroundMesh.load(SHOWCASE.biome);
      let first = 0, total = 0;
      await lib.load((d) => progress(d, total), (shipped) => {
        const show = TitleDiorama.assetNames(this.g.balance, shipped), hub = hubSet(shipped);
        first = show.size; total = new Set([...show, ...hub]).size;
        return show;
      });
      setAssetLibrary(lib);
      // M49: the diorama is built before the hub's models start decoding, not alongside them: the two together were
      // the boot's memory peak
      if (GroundMesh.available) await this.stageDiorama(title);
      await lib.ensure(hubSet(lib.shippedNames), (d) => progress(first + d, total));
    } else {
      await GroundMesh.load(hubBiome);                        // the tiling terrain maps; without them the ground is plain slabs
      // Only the hub's models at boot (render/assetNeeds.ts); each level loads its own set behind the cloud wipe.
      await lib.load(progress, this.g.keepAllHq ? undefined : hubSet);
    }
    await fonts;
    // The shop's premium skin (ui/shopSkin.ts): its fonts and painted chrome load in the background, off the boot path.
    void installKitArt();
    setAssetLibrary(lib);
    consoleLogger.log("info", `hq: ${lib.hqCount} of ${lib.shippedNames.size} models at boot, ${ktx2 && lib.usesKtx2 ? "KTX2" : "WebP"} textures`);
    if (DEBUG) (window as unknown as { __tdlib: unknown }).__tdlib = lib;
    // Warm the offline cache with the texture set this device loads, not both (models and terrain maps alike) — only in
    // the installed app: a browser tab loads each level's files as it is reached (infra/platform/shell.ts isInstalledWebApp).
    void offline.then((reg) => requestWholeGame(reg, lib.usesKtx2));
    // The next level's models start now, under the title: on 4G they are tens of seconds of download that the hub's
    // own delayed preload alone did not cover before the player's first PLAY.
    this.hubFlow.preloadNow();
    if (sheet) { removeBootSplash(); showReadabilitySheet(this.g.rig, this.g.ui); this.g.rig.frameLoop(); return; }
    audio.music("hub");
    // `?skiptitle` goes straight to the hub (the capture scripts' hook, whatever the save)
    if (!title) {
      removeBootSplash();
      // a battle the graphics reload interrupted is continued from its suspended copy (M33), not left for CONTINUE
      if (back?.reason === "graphics" && this.g.keeper.suspended && graphicsResumeAllowed()) {
        this.continueSuspended();
        window.setTimeout(() => toast(this.g.ui, reloadNoteText(back)), Game.NoticeDelayMs);
        return;
      }
      void Promise.resolve(this.showMenu()).then(() => { if (back) toast(this.g.ui, reloadNoteText(back)); else this.showBootNotice(); });
      return;
    }
    title.ready();
  }

  /**
   * Title / tap-to-start (drop 2 §1); the tap doubles as the audio unlock gesture. A brand-new player goes from it
   * straight into level 1; anyone else to the hub, in its own biome's ground (enterHubGround).
   */
  private makeTitle(live: boolean): TitleScreen {
    return new TitleScreen(this.g.ui, this.g.version, {
      onStart: () => {
        const leaveTitle = () => { this.screens.title?.dispose(); this.screens.title = null; this.leaveDiorama(); };
        // A brand-new player goes straight into level 1 (owner, 2026-09-24): its load holds the clouds like PLAY's.
        if (this.firstRun()) this.g.starts.run(() => cloudWipe(() => { leaveTitle(); return this.startLevel(this.hubFlow.nextLevel()); }, { hold: true }));
        else {
          const hold = !GroundMesh.isCached(FoliageView.biome);   // the hub's maps load under the clouds: hold them
          void cloudWipe(async () => { leaveTitle(); await this.enterHubGround(); return this.showMenu(); }, { hold }).then(() => this.showBootNotice());
        }
      },
      onSettings: () => showSettings(this.g.ui, this.settingsActions()),
    }, live);
  }

  /**
   * The showcase stands on the meadow's maps; a hub in another biome swaps them for its own (the old ones go first,
   * as a level start does: two biomes' maps at once is ~220 MiB).
   */
  private async enterHubGround(): Promise<void> {
    const biome = FoliageView.biome;
    if (GroundMesh.isCached(biome) && GroundMesh.currentBiome === biome) return;
    GroundMesh.drop();
    GroundMesh.release(biome);
    await GroundMesh.load(biome);
  }

  /** The title's live diorama (render/titleDiorama.ts), while it stands. */
  private diorama: TitleDiorama | null = null;

  /**
   * Builds the live title behind the still (its models are in: boot loaded them first), then lets the title fade
   * into it once a few frames are drawn. Never holds the boot up or the title's taps; a failure leaves the still.
   * `?stillshot`: held on its opening frame for the still's capture (tools/video/title_still.mjs).
   */
  private async stageDiorama(title: TitleScreen): Promise<void> {
    // resolves once the scene is built (`d.start()`); the wait for its first frames and the fade go on behind (M49)
    const still = () => this.screens.title === title && title.root.isConnected;
    try {
      // two frames for the title's own first paint before the build holds the main thread
      for (let i = 0; i < 2; i++) await new Promise((r) => requestAnimationFrame(r));
      if (!still()) return;
      const d = new TitleDiorama(this.g.rig, this.g.balance);
      this.diorama = d;
      d.frozen = new URLSearchParams(location.search).has("stillshot");
      d.start();
      // the first frames compile the shaders: the still stays over them until a few have been drawn
      void (async () => {
        while (d.frames < 4) { await new Promise((r) => requestAnimationFrame(r)); if (!still()) return; }
        if (!d.frozen) title.goLive(d);
      })().catch((err) => { consoleLogger.log("warning", `title diorama: ${err instanceof Error ? err.message : String(err)}`); this.leaveDiorama(); });
    } catch (err) {
      consoleLogger.log("warning", `title diorama: ${err instanceof Error ? err.message : String(err)}`);
      this.leaveDiorama();
    }
  }

  private leaveDiorama(): void {
    this.diorama?.dispose();
    this.diorama = null;
  }

  /**
   * A brand-new player: nothing cleared and no battle to continue, with Captain Bram on. The title then leads into
   * level 1, where his welcome opens the walkthrough; the hub is first seen after the first win. Without the guide
   * (`?noguide`: the smoke test) the hub comes first as before, since nothing would explain a battle out of nowhere.
   */
  private firstRun(): boolean {
    return guide() !== null && this.g.catalog.highestLevelCleared === 0 && this.g.keeper.suspended === undefined;
  }

  // ---------------------------------------------------------------- crash forensics and recovery (M33)

  /** What the hub says once after a boot that found the last session closed by iOS (null: nothing). */
  private bootNotice: string | null = null;
  static readonly NoticeDelayMs = 1200;
  private showBootNotice(): void {
    const text = this.bootNotice;
    this.bootNotice = null;
    if (text) window.setTimeout(() => toast(this.g.ui, text, { ms: 6000 }), Game.NoticeDelayMs);
  }

  /** Feeds the breadcrumb (infra/breadcrumbs.ts) once a second; it writes on a screen change or every 10 s. */
  private startBreadcrumbs(): void {
    window.setInterval(() => { try { breadcrumbs.update(this.crumbFacts()); } catch { /* forensics never break the game */ } }, 1000);
  }

  private crumbFacts(): CrumbFacts {
    const s = this.screens, lv = s.level, info = this.g.rig.renderer.info;
    const screen = lv ? (lv.isFinished ? "end" : "level") : s.title ? "title" : s.campaign ? "map" : s.hub ? "hub" : "loading";
    const frames = [...frameJournal.entries()].reverse().find((l) => l.kind === "frames");
    return {
      screen, level: lv?.sim.levelNumber ?? null, stage: lv?.sim.stage ?? null, tick: lv?.sim.tick ?? null,
      frames: frames ? journalLineText(frames) : null,
      gpu: { geoms: info.memory.geometries, texs: info.memory.textures, calls: this.g.rig.frameStats.calls },
      contexts: glContexts.labels(), battleSaved: this.g.keeper.suspended !== undefined,
    };
  }

  /**
   * The main canvas's WebGL context came back after a loss, but CPU copies the GPU held were released after upload
   * (render/cpuRelease.ts): what is on screen cannot be drawn again from memory. Rather than reload the page, every
   * screen is taken down, the library's models and the terrain maps are dropped, and the screen is built again from
   * the files (the offline cache): a battle under way from its suspended copy (replayed under the clouds, as
   * CONTINUE does), anything else back to the hub. One rebuild at a time.
   */
  private rebuilding = false;
  private rebuildGraphics(): void {
    if (this.rebuilding) return;
    this.rebuilding = true;
    frameJournal.note("graphics rebuilt after a lost context");
    const s = this.screens;
    let resume: number | null = null;
    try {
      resume = this.levelFlow.takeDownForRebuild();
      s.title?.dispose(); s.title = null; this.leaveDiorama();
      this.hubFlow.closeScreens();
      s.lib?.release(new Set());
      GroundMesh.drop();
      GroundMesh.release(null);
    } catch (err) {
      consoleLogger.log("error", `graphics rebuild failed (${err instanceof Error ? err.message : String(err)}); reloading`);
      reloadFor("graphics");
      return;
    }
    const done = () => { this.rebuilding = false; toast(this.g.ui, "The scene was redrawn after a hiccup. Your progress is safe"); };
    if (resume !== null && this.g.keeper.suspended) {
      this.continueSuspended();
      window.setTimeout(done, Game.NoticeDelayMs * 2);
      return;
    }
    FoliageView.biome = DetailScatter.biome = biomeOf(this.hubFlow.nextLevel());
    void cloudWipe(async () => { await this.enterHubGround(); return this.showMenu(); }, { hold: true }).then(done, done);
  }

  // ---------------------------------------------------------------- the page's lifecycle

  /**
   * The page is going away or out of sight (app switch, lock screen, a call, a tab close): a battle pauses and stays
   * paused when the player comes back. pagehide as well as visibilitychange, since WebKit does not always send the
   * latter when the page is put in the back-forward cache.
   */
  pageHidden(): void {
    this.g.keeper.suspend(this.screens.level);                // before anything else: iOS may never run this page again
    this.screens.level?.autoPause();
    this.g.keeper.pageHidden();
  }

  /** A user gesture: a wake lock the level wants but was refused (no activation yet) is asked for again. */
  gesture(): void { this.g.wakeLock.retry(); }

  /**
   * A newer build is live (play-testing through the tunnel: a rebuild on the Mac shows up on the phone by itself).
   * On the title nothing is under way, so the page reloads at once. Anywhere else the player is offered it — a toast
   * with UPDATE on the hub, again on every return to the hub — and the page reloads only on that tap, coming back to
   * the hub with "Updated" (infra/reloadNote.ts). It used to reload by itself as a battle ended, which dropped the
   * player on the launch screen with no word of why (M30 #21). A cold start picks the new build up anyway.
   */
  private updatePending = false;
  private onNewBuild(): void {
    this.updatePending = true;
    frameJournal.note("a newer build is live");
    if (this.screens.title !== null && this.screens.hub === null && this.screens.level === null) { location.reload(); return; }
    if (this.screens.level === null) this.offerUpdate();
  }
  offerUpdate(): void {
    if (!this.updatePending) return;
    // after the clouds have parted over the hub (the offer is called as the hub is asked for)
    window.setTimeout(() => {
      if (this.screens.level !== null) return;
      this.updateToast?.dismiss();
      this.updateToast = toast(this.g.ui, "A new version is ready", { ms: Game.UpdateToastMs, owner: null, action: { label: "UPDATE", onTap: () => reloadFor("update") } });
    }, Game.UpdateOfferDelayMs);
  }
  static readonly UpdateOfferDelayMs = 1500;
  private updateToast: ToastHandle | null = null;
  static readonly UpdateToastMs = 15_000;
}

// Mobile Safari ignores user-scalable=no: block page pinch-zoom and double-tap zoom explicitly.
document.addEventListener("gesturestart", (e) => e.preventDefault(), { passive: false });
document.addEventListener("touchmove", (e) => { if (e.touches.length > 1) e.preventDefault(); }, { passive: false });
document.addEventListener("dblclick", (e) => e.preventDefault(), { passive: false });
let lastTouchEnd = 0;
document.addEventListener("touchend", (e) => { const now = Date.now(); if (now - lastTouchEnd < 300 && (e.target as HTMLElement)?.tagName !== "BUTTON") e.preventDefault(); lastTouchEnd = now; }, { passive: false });

Settings.apply();
// Audio unlocks on the first gesture; UI buttons click; the audio setting mutes the port.
const unlock = () => { audio.unlock(); audio.setMuted(!Settings.current.audio); audio.setVolume(Settings.current.volume); };
document.addEventListener("pointerdown", unlock, { passive: true });
document.addEventListener("touchstart", unlock, { passive: true });
// WebKit only counts touchend/click as the activation that may resume audio; unlock also re-resumes after an interruption.
document.addEventListener("touchend", unlock, { passive: true });
document.addEventListener("click", unlock, { passive: true });
document.addEventListener("click", (e) => { if ((e.target as HTMLElement)?.closest("button")) audio.play("ui"); }, true);
onSettingsChange(() => { audio.setMuted(!Settings.current.audio); audio.setVolume(Settings.current.volume); });
// The save store opens first (IndexedDB is asynchronous; infra/save/openSaveStore.ts): the game reads the save at once.
void openSaveStore({ log: (level, message) => consoleLogger.log(level, message) }).then((saveStore) => {
  const game = new Game(document.getElementById("app")!, saveStore);
  installFrameJournal();
  document.addEventListener("visibilitychange", () => { breadcrumbs.visibility(document.hidden); if (document.hidden) game.pageHidden(); });
  window.addEventListener("pagehide", () => { game.pageHidden(); breadcrumbs.closed(); });
  window.addEventListener("pageshow", () => breadcrumbs.visibility(document.hidden));
  document.addEventListener("touchend", () => game.gesture(), { passive: true });
  document.addEventListener("click", () => game.gesture(), { passive: true });
  if (DEBUG) {
    // LevelLoading and cloudWipe: the captures stage the level-load war map on a scripted progress (tools/video)
    const td: Record<string, unknown> = { game, audio, ChestStage, LOOKS, GroundMesh, HqMaterials, LevelLoading, cloudWipe, CpuRelease, AssetLibrary, get level() { return game.currentLevel; } };   // __td.guide is installed by the guide controller (its debug hook)
    (window as unknown as { __td: unknown }).__td = td;
    // the banner studio (tools/art/banners): stages the Store/Shop banners with the game's own assets and renderer
    void import("./studio/bannerStudio").then((m) => { td.studio = m.bannerStudio(game.rig, () => (window as unknown as { __tdlib?: AssetLibrary }).__tdlib ?? null); });
    // M44: the leak / duplicate-render probe (infra/perfProbe.ts): __td.perf.start(), .sample(), .stop()
    perfProbe.setMain(game.rig.renderer, game.rig.scene);
    td.perf = perfProbe;
    // The icon renderer serves only the capture tools (tools/video/capture.mjs): fetched on demand, off the main bundle.
    void import("./render/iconRender").then(({ IconRender }) => { td.IconRender = IconRender; });
  }
}).catch(bootFailed);

/**
 * The game could not start (no WebGL, iOS out of contexts, an asset or save load that threw): a message with RETRY
 * and COPY DIAGNOSTICS instead of a blank page or a splash frozen mid-bar. Once: a second failure keeps the first.
 */
let bootFailedShown = false;
function bootFailed(err: unknown): void {
  consoleLogger.log("error", `boot failed: ${err instanceof Error ? `${err.message}\n${err.stack ?? ""}` : String(err)}`);
  if (bootFailedShown) return;
  bootFailedShown = true;
  showBootError(document.getElementById("app") ?? document.body, err, { retry: () => location.reload(), copyDiagnostics });
}

/**
 * Production builds register the offline cache (packages/web/sw/sw.js) before the assets load, so on a first visit the
 * worker takes the page over part-way through and caches what follows; once the game has booted it is asked to fetch
 * the rest in the background (files already in the HTTP cache revalidate with a 304, not a second download), so the
 * next launch works without a signal. Skipped in dev, under automation (the capture tools pump frames by hand) and
 * outside a secure context, where browsers refuse service workers anyway.
 */
async function registerOfflineCache(): Promise<ServiceWorkerRegistration | null> {
  if (!import.meta.env.PROD || navigator.webdriver || !("serviceWorker" in navigator) || !window.isSecureContext) return null;
  try {
    await navigator.serviceWorker.register("./sw.js");
    // The offline copy of the game is a few hundred MB: ask that the browser keep it rather than evict it under storage
    // pressure (granted outright to a home-screen app on most browsers; a refusal changes nothing).
    void navigator.storage?.persist?.().catch(() => false);
    return await navigator.serviceWorker.ready;
  } catch (err) {
    console.warn("offline cache unavailable", err);
    return null;
  }
}

/**
 * A battle the graphics reload interrupted is continued at once — once per GraphicsResumeEveryMs: a context lost again
 * and again (the GPU short of memory) must not loop the player through reload and replay; the hub's CONTINUE stays.
 */
const GraphicsResumeKey = "highmarch.gfxResumeAt", GraphicsResumeEveryMs = 5 * 60_000;
function graphicsResumeAllowed(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(GraphicsResumeKey) ?? "0");
    if (now - last < GraphicsResumeEveryMs && now >= last) return false;
    sessionStorage.setItem(GraphicsResumeKey, String(now));
  } catch { /* no storage: allow it */ }
  return true;
}
